import { useTranslation } from 'react-i18next';
import { KpiCard } from '@/shared/components/dashboard/KpiCard';
import { EmptyPanel, ErrorPanel, LoadingPanel } from '@/shared/components/dashboard/panels';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import type { WebsiteAnalyticsProductSummaryItem } from '../types';
import { formatPercentRate } from '../utils/format';

interface ProductAnalyticsProductCardsProps {
  items: WebsiteAnalyticsProductSummaryItem[];
  selectedProductSlug: string;
  isLoading: boolean;
  isError: boolean;
}

function ProductSummaryCard({ item }: { item: WebsiteAnalyticsProductSummaryItem }) {
  const { t } = useTranslation();

  const metrics = [
    { label: t('websiteActivity.products.metrics.visitors'), value: item.visitors },
    { label: t('websiteActivity.products.metrics.pageViews'), value: item.pageViews },
    { label: t('websiteActivity.products.metrics.registrations'), value: item.registrations },
    { label: t('websiteActivity.products.metrics.freeTrials'), value: item.freeTrials },
    { label: t('websiteActivity.products.metrics.failedTrials'), value: item.failedTrials },
    { label: t('websiteActivity.products.metrics.paymentsStarted'), value: item.paymentsStarted },
    { label: t('websiteActivity.products.metrics.payments'), value: item.payments },
  ];

  const rates = [
    {
      label: t('websiteActivity.products.rates.visitToRegistration'),
      value: formatPercentRate(item.visitToRegistrationRate),
    },
    {
      label: t('websiteActivity.products.rates.registrationToTrial'),
      value: formatPercentRate(item.registrationToTrialRate),
    },
    {
      label: t('websiteActivity.products.rates.trialToPayment'),
      value: formatPercentRate(item.trialToPaymentRate),
    },
  ];

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold">{item.productSlug}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {metrics.map((metric) => (
            <div key={metric.label}>
              <p className="text-xs text-muted-foreground">{metric.label}</p>
              <p className="text-lg font-bold tabular-nums">{metric.value}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 gap-2 border-t border-border pt-3 sm:grid-cols-3">
          {rates.map((rate) => (
            <div key={rate.label}>
              <p className="text-xs text-muted-foreground">{rate.label}</p>
              <p className="text-sm font-semibold tabular-nums">{rate.value}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export function ProductAnalyticsProductCards({
  items,
  selectedProductSlug,
  isLoading,
  isError,
}: ProductAnalyticsProductCardsProps) {
  const { t } = useTranslation();

  const visibleItems = selectedProductSlug
    ? items.filter((item) => item.productSlug === selectedProductSlug)
    : items;

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-foreground">
        {t('websiteActivity.products.summaryTitle')}
      </h3>
      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {Array.from({ length: selectedProductSlug ? 1 : 2 }).map((_, index) => (
            <div key={index} className="rounded-xl border border-border bg-card p-5 animate-pulse">
              <div className="h-4 w-32 rounded bg-muted" />
              <div className="mt-4 grid grid-cols-3 gap-3">
                {Array.from({ length: 6 }).map((__, cellIndex) => (
                  <div key={cellIndex} className="h-10 rounded bg-muted" />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : isError ? (
        <ErrorPanel message={t('websiteActivity.products.summaryLoadError')} />
      ) : visibleItems.length === 0 ? (
        <EmptyPanel
          title={t('websiteActivity.products.summaryEmptyTitle')}
          message={t('websiteActivity.products.summaryEmptyMessage')}
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {visibleItems.map((item) => (
            <ProductSummaryCard key={item.productSlug} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

interface ProductAnalyticsConversionPanelProps {
  visitors: number;
  registrations: number;
  freeTrials: number;
  payments: number;
  visitToRegistrationRate: number;
  registrationToTrialRate: number;
  trialToPaymentRate: number;
  isLoading: boolean;
  isError: boolean;
}

export function ProductAnalyticsConversionPanel({
  visitors,
  registrations,
  freeTrials,
  payments,
  visitToRegistrationRate,
  registrationToTrialRate,
  trialToPaymentRate,
  isLoading,
  isError,
}: ProductAnalyticsConversionPanelProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground">
          {t('websiteActivity.products.conversionTitle')}
        </h3>
        <LoadingPanel className="py-8" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground">
          {t('websiteActivity.products.conversionTitle')}
        </h3>
        <ErrorPanel message={t('websiteActivity.products.conversionLoadError')} />
      </div>
    );
  }

  const countCards = [
    {
      title: t('websiteActivity.products.metrics.visitors'),
      value: String(visitors),
      tone: 'cyan' as const,
    },
    {
      title: t('websiteActivity.products.metrics.registrations'),
      value: String(registrations),
      tone: 'blue' as const,
    },
    {
      title: t('websiteActivity.products.metrics.freeTrials'),
      value: String(freeTrials),
      tone: 'green' as const,
    },
    {
      title: t('websiteActivity.products.metrics.payments'),
      value: String(payments),
      tone: 'violet' as const,
    },
  ];

  const rateCards = [
    {
      title: t('websiteActivity.products.rates.visitToRegistration'),
      value: formatPercentRate(visitToRegistrationRate),
    },
    {
      title: t('websiteActivity.products.rates.registrationToTrial'),
      value: formatPercentRate(registrationToTrialRate),
    },
    {
      title: t('websiteActivity.products.rates.trialToPayment'),
      value: formatPercentRate(trialToPaymentRate),
    },
  ];

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-foreground">
        {t('websiteActivity.products.conversionTitle')}
      </h3>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {countCards.map((card) => (
          <KpiCard key={card.title} title={card.title} value={card.value} tone={card.tone} />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {rateCards.map((card) => (
          <KpiCard key={card.title} title={card.title} value={card.value} tone="amber" />
        ))}
      </div>
    </div>
  );
}
